import React from 'react';

import { cn } from 'revibe-ui';
import { I18N_NAMESPACES } from 'i18n';
import { useTranslation } from 'i18n/hooks';
import { SpacedContainer } from 'revibe-ui';
import { SHOP_ROUTES } from 'routes';

import { Image } from 'modules/common/components/ImageLazyload';
import { useCarouselInterval } from 'modules/common/hooks/useCarouselInterval';
import { getDynamicRouteUrl } from 'modules/common/utils';

import { ClampText } from 'ui/components/ClampText';
import { Link } from 'ui/components/Link';
import { Tag } from 'ui/components/Tag';
import { MarketplaceAPI } from 'revibe-api';

type Props = { collection: MarketplaceAPI.ItemCollection; learnMore?: boolean };

export const ItemCollectionBanner = ({
  collection,
  learnMore = false,
}: Props) => {
  const { tu } = useTranslation(I18N_NAMESPACES.COLLECTION);
  const { index, visitedIndexCount } = useCarouselInterval(
    collection?.photos.length || 0
  );

  return (
    <div className="flex grid-cols-2 flex-col gap-4 md:grid">
      <SpacedContainer className="bg-white px-4 py-10">
        <SpacedContainer className="text-black">
          <div>
            <h1 className="font-formula text-3xl md:text-4xl lg:text-5xl">
              {collection.name}
            </h1>
            <p className="text-sm text-gray-400">
              {tu('release-date')}: {collection.date}
            </p>
          </div>
          {learnMore && (
            <Link
              hasDecoration={false}
              to={getDynamicRouteUrl(SHOP_ROUTES.COLLECTION, {
                slug: collection.slug,
              })}
            >
              <Tag> {tu('learn-more', 'common')}</Tag>
            </Link>
          )}
          <ClampText lines={6}>
            <p className="text-slate-700">{collection.description}</p>
          </ClampText>
        </SpacedContainer>
        {collection.collab.is_collab && (
          <div className="mt-8">
            <div className="flex grid-cols-2 flex-col gap-4 md:grid">
              <div>
                <h3 className="text-lg">
                  {tu('about-brand', '', {
                    brand: collection.seller.name,
                  })}
                </h3>
                <ClampText lines={6}>
                  <p className="text-sm text-slate-700">
                    {collection.seller.description}
                  </p>
                </ClampText>
              </div>
              <div>
                <h3 className="text-lg">
                  {tu('about-brand', '', {
                    brand: collection.collab.brand_name,
                  })}
                </h3>
                <ClampText lines={6}>
                  <p className="text-sm  text-slate-700">
                    {collection.collab.brand_description}
                  </p>
                </ClampText>
              </div>
            </div>
          </div>
        )}
      </SpacedContainer>
      {collection.photos.length > 0 && (
        <div className="relative box-border flex min-h-full flex-1 flex-col items-end overflow-hidden">
          {collection.photos.map((photo, i) =>
            i < visitedIndexCount || i <= index + 1 ? (
              <div
                className={cn(
                  'absolute ml-auto h-full w-fit transition-opacity  lg:border-l',
                  i === index ? 'opacity-100' : 'opacity-0'
                )}
                key={photo.medium_link}
              >
                <Image
                  className="border-l border-black"
                  objectFit="contain"
                  src={photo.medium_link}
                  alt={collection?.name}
                />
              </div>
            ) : (
              <img
                className="hidden"
                data-src={photo.medium_link}
                alt={collection?.name}
                key={photo.medium_link}
              />
            )
          )}
        </div>
      )}
    </div>
  );
};
